import React, { useEffect, useState } from 'react'
import { Modal, Form, DatePicker } from 'antd'
import styled from 'styled-components'
import moment from 'moment'
import { getSaveReceiptConfirmMeta } from 'src/utils/goodsReceiptingCreditHelpers'
import { getGrLineOutcomeProgress } from 'src/utils/goodsReceiptingActionHelpers'
import { buildReceiptImpact } from 'src/utils/receiptDiscrepancyGuidance'
import { creditMoney } from 'src/utils/invoiceCreditDiscrepancy'
import {
  ACTUAL_DELIVERY_DATE_REQUIRED_MESSAGE,
  parseActualDeliveryDate,
  validateActualDeliveryDate,
} from 'src/utils/actualDeliveryDate'

/**
 * Save-receipt confirm — Actual Delivery Date required (blank by default).
 * Does not invent today's date. Invoice number/date stay on attach flows.
 */
const SaveReceiptConfirmModal = ({
  visible,
  record,
  editedReceipting = [],
  asOrderedLineIds = [],
  lineOrders = [],
  weightedItems = [],
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [actualDeliveryDate, setActualDeliveryDate] = useState(null)
  const [dateError, setDateError] = useState('')

  // Seed from an existing receipt date when correcting; otherwise stay blank.
  useEffect(() => {
    if (!visible) return
    const existing = parseActualDeliveryDate(record?.actualDeliveryDate)
    setActualDeliveryDate(
      existing.ok ? moment(existing.ymd, 'YYYY-MM-DD') : null,
    )
    setDateError('')
  }, [visible, record])

  if (!record) return null

  const meta = getSaveReceiptConfirmMeta(record, editedReceipting)
  const progress = getGrLineOutcomeProgress(
    lineOrders,
    editedReceipting,
    asOrderedLineIds,
  )
  const impacts = buildReceiptImpact(
    record,
    editedReceipting,
    lineOrders,
    weightedItems,
  )

  const handleOk = () => {
    const check = validateActualDeliveryDate(actualDeliveryDate, {
      required: true,
    })
    if (!check.ok) {
      setDateError(check.message || ACTUAL_DELIVERY_DATE_REQUIRED_MESSAGE)
      return
    }
    onConfirm(meta.saveType, check.ymd)
  }

  const handleCancel = () => {
    setActualDeliveryDate(null)
    setDateError('')
    onCancel()
  }

  return (
    <StyledModal
      title={meta.title}
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={meta.okText}
      cancelText='Cancel'
      okButtonProps={{ type: 'primary', loading }}
      confirmLoading={loading}
      destroyOnClose
    >
      <OrderDetails>
        <DetailRow>Supplier: {record.supplier?.title || '—'}</DetailRow>
        <DetailRow>PO Number: {record.purchaseOrderNumber || '—'}</DetailRow>
        <DetailRow>
          Ordered Amount (inc VAT): £
          {((record.totalSpendOrdered || 0) / 100).toFixed(2)}
        </DetailRow>
      </OrderDetails>

      <Form layout='vertical'>
        <Form.Item
          label='Actual Delivery Date'
          required
          validateStatus={dateError ? 'error' : ''}
          help={
            dateError ||
            'When the goods physically arrived (not today by default).'
          }
        >
          <StyledDatePicker
            value={actualDeliveryDate}
            onChange={value => {
              setActualDeliveryDate(value)
              if (dateError) setDateError('')
            }}
            format='DD/MM/YYYY'
            placeholder='Select the date goods arrived'
            style={{ width: '100%' }}
            disabledDate={current =>
              current && current.isAfter(moment().endOf('day'))
            }
            allowClear
          />
        </Form.Item>
      </Form>

      <Description>{meta.description}</Description>

      {progress.total > 0 && progress.unresolved > 0 && (
        <ProgressNote>
          {progress.resolved} of {progress.total} lines completed —{' '}
          {progress.unresolved} line{progress.unresolved === 1 ? '' : 's'} still
          require{progress.unresolved === 1 ? 's' : ''} an outcome (mark As
          ordered or Report Error before completing the receipt).
        </ProgressNote>
      )}

      {impacts.length > 0 && (
        <ImpactList>
          <p>
            Check the receipt changes below. Values use the receipt unit prices,
            before any additional VAT or PO discount.
          </p>
          {impacts.map(line => (
            <Impact key={line.id}>
              <strong>
                {line.title} — {line.reason}
              </strong>
              <span>
                Ordered: {line.orderedQty ?? '—'} ×{' '}
                {creditMoney(line.orderedPrice)} ={' '}
                {creditMoney(line.orderedValue)}
              </span>
              <span>
                Received after save: {line.receivedQty ?? '—'} ×{' '}
                {creditMoney(line.receivedPrice)} ={' '}
                {creditMoney(line.receivedValue)}
              </span>
              <span>
                Receipt value reduction: {creditMoney(line.reductionPennies)}
              </span>
              <span>
                {line.askForCredit
                  ? 'Credit requested: supplier review of this line and your notes. The receipt value reduction is not a confirmed supplier credit.'
                  : 'No credit requested for this line.'}
              </span>
              {line.notes && <span>Notes: {line.notes}</span>}
            </Impact>
          ))}
        </ImpactList>
      )}

      {meta.willRequestCredit ? (
        <CreditNote>
          Credit request email will be sent for {meta.creditLineCount} line
          {meta.creditLineCount === 1 ? '' : 's'} (Ask for credit?).
        </CreditNote>
      ) : (
        <NoCreditNote>No credit request email for this save.</NoCreditNote>
      )}
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  max-width: calc(100vw - 24px);
  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
  }
  .ant-modal-content {
    border-radius: 8px;
  }
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`

const ImpactList = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 16px;
`
const Impact = styled.div`
  display: grid;
  gap: 5px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow-wrap: anywhere;
`

const OrderDetails = styled.div`
  margin-bottom: 14px;
`

const DetailRow = styled.p`
  margin: 4px 0;
  color: #595959;
  font-size: 13px;
`

const Description = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.75);
`

const CreditNote = styled.p`
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #874d00;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
`

const NoCreditNote = styled.p`
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #135200;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
`

const ProgressNote = styled.p`
  margin: 0 0 12px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #874d00;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
`

export default SaveReceiptConfirmModal
